import { Typography } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useStyles } from "../RCACompliance.styles";
import { RCAAPComplianceEntity } from "entities/RCA/RCAAPComplianceEntity/RCAAPComplianceEntity";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { RCAAPComplianceContext } from "contexts/RCAAPComplianceContext/RCAAPComplianceContext";
import RCAWithNoActionPlansModal from "./RCAWithNoActionPlansModal";
import { IAPAvgPerUser } from "entities/RCA/RCAAPComplianceEntity/RCAAPComplianceEntityInterfaces";

const RCAActionPlansPerRCA = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { filters } = useContext(RCAAPComplianceContext);
    const [totalAvg, setTotalAvg] = useState(0);
    const [totalRcaWithZeroAP, setTotalRcaWithZeroAP] = useState(0);
    const [userSelected, setUserSelected] = useState<IAPAvgPerUser>();
    const [openRCAWithNoActionPlansModal, setOpenRCAWithNoActionPlansModal] = useState<boolean>(false);
    const argsFilters = useMemo(
        () => ({
            date: moment().subtract(Number(filters.periodOption), "day").startOf("day").toISOString(),
            meetingTypeIds: filters.meetingTypeIds,
            totalMeetingTypes: filters.totalMeetingTypes,
        }),
        [filters],
    );
    const { apAvgPerUser } = RCAAPComplianceEntity.GetAPAvgPerUser(argsFilters);

    useEffect(() => {
        if (apAvgPerUser) {
            const sum = apAvgPerUser.reduce((acc, user) => acc + user.average_action_plans_per_rca, 0);
            const average = sum / apAvgPerUser.length;
            setTotalAvg(average);
            setTotalRcaWithZeroAP(apAvgPerUser.reduce((acc, user) => acc + user.rca_without_action_plans, 0));
        }
    }, [apAvgPerUser]);

    return (
        <>
            <div className={classes.card}>
                <div className={classes.tableHeader}>
                    <Typography variant="rcaTitle" className={classes.cardTitle}>
                        {t("rca.compliance.rca_compliance.ap_per_rca.title")}
                    </Typography>
                </div>
                <table className={classes.table}>
                    {apAvgPerUser.length > 0 ? (
                        <>
                            <thead>
                                <tr>
                                    <th className={classes.th}>{t("rca.compliance.rca_compliance.ap_per_rca.user")}</th>
                                    <th className={classes.th}>{t("rca.compliance.rca_compliance.ap_per_rca.avg")}</th>
                                    <th className={classes.th}>
                                        {t("rca.compliance.rca_compliance.ap_per_rca.rcaWithZeroAp")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {apAvgPerUser?.map((user) => (
                                    <tr key={user.user_id}>
                                        <td className={classes.thTd}>
                                            <div className={classes.userNameContainer}>
                                                {user.media_url ? (
                                                    <img
                                                        src={user.media_url}
                                                        alt={`${user.first_name} ${user.last_name}`}
                                                        className={classes.userImage}
                                                    />
                                                ) : (
                                                    <AccountCircleIcon className={classes.userImage} />
                                                )}
                                                {user.first_name} {user.last_name}
                                            </div>
                                        </td>
                                        <td className={classes.thTd}>{user.average_action_plans_per_rca}</td>
                                        <td
                                            onClick={() => {
                                                setUserSelected(user);
                                                setOpenRCAWithNoActionPlansModal(true);
                                            }}
                                            className={
                                                user.rca_without_action_plans ? classes.thTdHighlighted : classes.thTd
                                            }
                                        >
                                            {user.rca_without_action_plans ?? "-"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className={classes.tfootTd}>
                                        {t("rca.compliance.rca_compliance.ap_per_rca.total")}
                                    </td>
                                    <td className={classes.tfootTd}>{totalAvg}</td>
                                    <td className={classes.tfootTd}>{totalRcaWithZeroAP}</td>
                                </tr>
                            </tfoot>
                        </>
                    ) : (
                        <p className={classes.noData}>{t("rca.compliance.rca_compliance.ap_per_rca.noDataFound")}</p>
                    )}
                </table>
            </div>
            <RCAWithNoActionPlansModal
                open={openRCAWithNoActionPlansModal}
                handleClose={() => setOpenRCAWithNoActionPlansModal(false)}
                data={userSelected}
            />
        </>
    );
};

export default RCAActionPlansPerRCA;
